import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import configEntzy from "components/config/ConfigEntzy";
import { MoreButton } from "components/utils/common/CommonButtons";

function formatDebugValue(value) {
  if (!value) {
    return "No response captured yet.";
  }

  return JSON.stringify(value, null, 2);
}

function CnxDebug({
  open,
  onClose,
  bootstrapDraft,
  setBootstrapDraft,
  bootstrapOutcome,
  debugState,
  sessionReady,
  sessionForm,
  setSessionForm,
  persistedSessionDebug,
  selectedConversation,
  selectedConversationKey,
  setSelectedConversationKey,
  ciphertext,
  nonce,
  handleGenerateConversationKey,
  activity,
  steps,
  fieldSx,
  subtlePanelSx,
  loadingAction,
}) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 520, lg: 640 },
          color: "#eef2ff",
          background:
            "linear-gradient(180deg, rgba(11,12,18,0.98), rgba(7,8,12,0.99))",
          backgroundColor: "#090a0f",
          borderLeft: "1px solid rgba(255,255,255,0.08)",
        },
      }}
    >
      <Box sx={{ p: configEntzy.APP_SPACING_MD }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{ justifyContent: "space-between", alignItems: { sm: "center" } }}
          >
            <Box>
              <Typography className="cnx-exp-eyebrow" variant="body2">
                Debug And Activity
              </Typography>
              <Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>
                Provider Diagnostics Drawer
              </Typography>
            </Box>
            <MoreButton
              variant="contained"
              size="large"
              type="button"
              color="bright"
              text="Close Debug Drawer"
              onClick={onClose}
            />
          </Stack>

          <Typography variant="body2" sx={{ opacity: 0.82 }}>
            This keeps the provider-bootstrap diagnostics available without competing
            visually with the main activation and chat UI.
          </Typography>

          <TextField
            fullWidth
            label="Provider User ID"
            placeholder="Entzy stable user ID"
            value={bootstrapDraft.providerUserId}
            sx={fieldSx}
            onChange={(event) =>
              setBootstrapDraft((current) => ({
                ...current,
                providerUserId: event.target.value,
              }))
            }
          />
          <TextField
            fullWidth
            label="Email"
            placeholder="user@example.com"
            value={bootstrapDraft.email}
            sx={fieldSx}
            onChange={(event) =>
              setBootstrapDraft((current) => ({
                ...current,
                email: event.target.value,
              }))
            }
          />
          <TextField
            fullWidth
            label="Name"
            placeholder="Name"
            value={bootstrapDraft.name}
            sx={fieldSx}
            onChange={(event) =>
              setBootstrapDraft((current) => ({
                ...current,
                name: event.target.value,
              }))
            }
          />

          <Box sx={subtlePanelSx}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Current activation outcome
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.82 }}>
              {bootstrapOutcome}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.72 }}>
              Lookup priority should be provider user id first, then email as a secondary
              hint. If no account exists, the provider should offer registration with
              optional prefilled CNX pointer suggestions.
            </Typography>
          </Box>

          <Box sx={subtlePanelSx}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Proof checklist
            </Typography>
            <Stack spacing={1.25} sx={{ mt: configEntzy.APP_SPACING_MD }}>
              {steps.map((step) => (
                <Box key={step.id} sx={subtlePanelSx}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {step.title} [{step.status}]
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.82 }}>
                    {step.detail}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          <Box sx={subtlePanelSx}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Current backend response
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.82 }}>
              Last action: {debugState.lastAction}
            </Typography>
            {debugState.lastError ? (
              <Typography variant="body2" sx={{ mt: 1, color: "#ffb3b3" }}>
                Error: {debugState.lastError}
              </Typography>
            ) : null}
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.72 }}>
              Raw GraphQL/service response
            </Typography>
            <Box component="pre" className="cnx-exp-debug-pre">
              {formatDebugValue(debugState.rawResponse)}
            </Box>
            <Typography variant="body2" sx={{ mt: 1.5, opacity: 0.72 }}>
              Normalized activation result
            </Typography>
            <Box component="pre" className="cnx-exp-debug-pre">
              {formatDebugValue(debugState.normalizedResponse)}
            </Box>
            <Typography variant="body2" sx={{ mt: 1.5, opacity: 0.72 }}>
              Session restore diagnostics
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.82 }}>
              Session ready: {sessionReady ? "yes" : "no"}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.82 }}>
              Provider session code present: {sessionForm.providerSessionId ? "yes" : "no"}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.82 }}>
              Saved local session present: {persistedSessionDebug.hasSavedSession ? "yes" : "no"}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.82 }}>
              Active loading action: {loadingAction || "idle"}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.72 }}>
              In-memory session draft
            </Typography>
            <Box component="pre" className="cnx-exp-debug-pre">
              {formatDebugValue(sessionForm)}
            </Box>
            <Typography variant="body2" sx={{ mt: 1.5, opacity: 0.72 }}>
              Saved localStorage session
            </Typography>
            <Box component="pre" className="cnx-exp-debug-pre">
              {persistedSessionDebug.savedSessionText}
            </Box>
            <Typography variant="body2" sx={{ mt: 1.5, opacity: 0.72 }}>
              listConversations status
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.82 }}>
              {debugState.conversationLoad?.status || "idle"}
            </Typography>
            {debugState.conversationLoad?.lastError ? (
              <Typography variant="body2" sx={{ mt: 1, color: "#ffb3b3" }}>
                Error: {debugState.conversationLoad.lastError}
              </Typography>
            ) : null}
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.72 }}>
              Raw listConversations response
            </Typography>
            <Box component="pre" className="cnx-exp-debug-pre">
              {formatDebugValue(debugState.conversationLoad?.rawResponse)}
            </Box>
            <Typography variant="body2" sx={{ mt: 1.5, opacity: 0.72 }}>
              Normalized listConversations response
            </Typography>
            <Box component="pre" className="cnx-exp-debug-pre">
              {formatDebugValue(debugState.conversationLoad?.normalizedResponse)}
            </Box>
          </Box>

          {selectedConversation ? (
            <Box sx={subtlePanelSx}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Proof encryption diagnostics
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.82 }}>
                Active conversation: {selectedConversation.title || selectedConversation.id}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.72 }}>
                Use this area for proof-only key alignment across browsers until CNX-side wrapped
                conversation-key distribution is available.
              </Typography>
              <TextField
                fullWidth
                sx={{ ...fieldSx, mt: 2 }}
                label="Shared conversation key"
                placeholder="Proof-only base64 room key"
                value={selectedConversationKey}
                onChange={(event) => setSelectedConversationKey(event.target.value)}
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1.25 }}>
                <MoreButton
                  variant="contained"
                  size="small"
                  type="button"
                  color="bright"
                  className="cnx-exp-nav-button"
                  text="Generate Key"
                  onClick={handleGenerateConversationKey}
                />
              </Stack>
              <TextField
                fullWidth
                multiline
                minRows={3}
                sx={{ ...fieldSx, mt: 1.5 }}
                label="Latest ciphertext envelope"
                placeholder="Generated encrypted payload"
                value={ciphertext}
                InputProps={{ readOnly: true }}
              />
              <TextField
                fullWidth
                sx={{ ...fieldSx, mt: 1.5 }}
                label="Latest nonce / IV"
                placeholder="Encryption nonce"
                value={nonce}
                InputProps={{ readOnly: true }}
              />
            </Box>
          ) : null}

          {!sessionReady ? (
            <>
              <Divider />
              <Typography variant="body2" sx={{ opacity: 0.82 }}>
                Session restore override
              </Typography>
              <TextField
                fullWidth
                label="CNX API Base URL"
                placeholder="https://api.develop.cnx.to"
                value={sessionForm.baseUrl}
                sx={fieldSx}
                onChange={(event) =>
                  setSessionForm((current) => ({
                    ...current,
                    baseUrl: event.target.value,
                  }))
                }
              />
              <TextField
                fullWidth
                label="Provider Session Code (Optional)"
                placeholder="Opaque provider-backed session id for restore testing"
                value={sessionForm.providerSessionId || ""}
                sx={fieldSx}
                onChange={(event) =>
                  setSessionForm((current) => ({
                    ...current,
                    providerSessionId: event.target.value,
                  }))
                }
              />
              <TextField
                fullWidth
                label="CNX Account ID (Optional)"
                placeholder="acct_..."
                value={sessionForm.accountId}
                sx={fieldSx}
                onChange={(event) =>
                  setSessionForm((current) => ({
                    ...current,
                    accountId: event.target.value,
                  }))
                }
              />
            </>
          ) : null}

          <Divider />
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Activity
          </Typography>
          <Stack spacing={1}>
            {activity.length ? (
              activity.map((line, index) => (
                <Typography key={`${line}-${index}`} variant="body2">
                  {line}
                </Typography>
              ))
            ) : (
              <Typography variant="body2" sx={{ opacity: 0.72 }}>
                Activity will appear here as the CNX proof flow is prepared.
              </Typography>
            )}
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
}

export default CnxDebug;
